import { WithOwners } from 'entities/modules/users';
import { executeApiCall } from 'services/api';
import { getLoaders } from 'entities/hooks/load';
import {
  ClosureFormData,
  FullProject,
  Project,
  ProjectFile,
  ProjectLog,
  ProjectSinglePropertyChangeHistory,
  TaskFile,
} from '../types';

export interface ProjectsByStatusLoadParams {
  status: string;
  startDate?: string;
  endDate?: string;
  team?: string;
  paginationToken?: string;
  ref?: 'STATUS' | 'TEAM' | 'OWN';
}

export const projectsLoaders = getLoaders({
  listProjectsByStatus: {
    load: async (
      payload: ProjectsByStatusLoadParams,
    ): Promise<{ projects: FullProject[]; paginationToken?: string }> => {
      return executeApiCall({
        path: `/projects/status`,

        method: 'get',

        payload,
      });
    },
  },

  fullProject: {
    load: async ({ id }: { id: string }): Promise<FullProject> => {
      return executeApiCall({
        path: `/projects/${id}`,

        method: 'get',
      });
    },
  },

  batchGetProjects: {
    load: async ({ ids }: { ids: string[] }): Promise<FullProject[]> => {
      if (!ids.length) return [];

      return executeApiCall({
        path: `/projects`,

        method: 'get',

        payload: {
          ids: ids.join(','),
        },
      });
    },
  },

  projectLogs: {
    load: async ({ id }: { id: string }): Promise<ProjectLog[]> => {
      return executeApiCall({
        path: `/projects/${id}/logs`,

        method: 'get',
      });
    },
  },

  unassignedProjects: {
    load: async (): Promise<Project[]> => {
      return executeApiCall({
        path: `/projects/unassigned`,

        method: 'get',
      });
    },
  },

  allActiveProjects: {
    load: async (): Promise<FullProject[]> => {
      return executeApiCall<FullProject[]>({
        path: `/projects`,

        method: 'get',
      });
    },
  },

  allActiveProjectsV2: {
    load: async (): Promise<FullProject[]> => {
      const SPLIT_COUNT = 2;
      const splits = Array.from({ length: SPLIT_COUNT }, (_, index) => index);

      const projects = await Promise.all(
        splits.map(async (splitId) =>
          executeApiCall<FullProject[]>({
            path: `/projects`,

            method: 'get',

            payload: {
              loadType: 'SPLIT',
              splitCount: SPLIT_COUNT,
              splitId,
            },
          }),
        ),
      );

      return projects.flat();
    },
  },

  closureForm: {
    load: async ({ projectId }: { projectId: string }): Promise<ClosureFormData> => {
      return executeApiCall({
        path: `/projects/project/${projectId}/closure-form`,

        method: 'get',
      });
    },
  },

  activeTrackerOrigins: {
    load: async (): Promise<
      {
        id: string;
        name: string;
        wavesLeft: number;
        waves: number;
      }[]
    > => {
      return executeApiCall({
        path: '/projects/tracker-origins',

        method: 'get',
      });
    },
  },

  projectOriginWavesLeft: {
    load: async (payload: {
      origin: string;
    }): Promise<{
      wavesLeft: number;
    }> => {
      return executeApiCall({
        path: '/projects/tracker',

        payload,

        method: 'get',
      });
    },
  },

  existingTrackerWaves: {
    load: async (payload: { origin: string }): Promise<WithOwners<Project>[]> => {
      return executeApiCall({
        path: '/projects/tracker/all-waves',

        payload,

        method: 'get',
      });
    },
  },

  projectSampleIds: {
    load: async ({ projectId }: { projectId: string }): Promise<string[]> => {
      return executeApiCall({
        path: `/projects/project/${projectId}/sample-ids`,

        method: 'get',
      });
    },
  },

  projectPropertyHistory: {
    load: async (payload: {
      project: string;
      property: string;
    }): Promise<ProjectSinglePropertyChangeHistory[]> => {
      return executeApiCall({
        path: `/projects/property-history`,

        method: 'get',

        payload,
      });
    },
  },

  allProjectFiles: {
    load: async (payload: { projectId: string }): Promise<Array<ProjectFile | TaskFile>> => {
      return executeApiCall({
        path: `/projects/files`,

        method: 'get',

        payload,
      });
    },
  },
});
